import { memo, useMemo } from "react";
import { Avatar, Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IInputFile, useFormContext } from "@4uhub/lib4uhub";

import { HEX_COLOR, TRegisterForm } from "../RegisterSchema";

const translationPath = "page.register.applications.";

export const getFileSrc = (file?: IInputFile | null) => {
  if (!file) return undefined;

  if (file.cdnDomain && file.filePath) {
    return file.cdnDomain + "/" + file.filePath;
  }

  if (file.file) {
    return URL.createObjectURL(file.file);
  }
};

const SiteAppChatProfilePreview = ({
  isExternalSite,
}: {
  isExternalSite: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { control } = useFormContext<TRegisterForm>();

  const [
    chatTitle,
    chatSubtitle,
    chatBotName,
    chatPrimaryColor,
    chatSecondaryColor,
    chatLogo,
    chatBotAvatar,
  ] = useWatch({
    control,
    name: [
      "chatTitle",
      "chatSubtitle",
      "chatBotName",
      "chatPrimaryColor",
      "chatSecondaryColor",
      "chatLogo",
      "chatBotAvatar",
    ],
  });

  const logoSrc = useMemo(() => getFileSrc(chatLogo as IInputFile), [chatLogo]);
  const botAvatarSrc = useMemo(
    () => getFileSrc(chatBotAvatar as IInputFile),
    [chatBotAvatar]
  );

  // getContrastText lança erro para cores inválidas, então só aplica hex completo
  const colorStyle = (color?: string | null) =>
    isExternalSite && color && HEX_COLOR.test(color)
      ? { bgcolor: color, color: theme.palette.getContrastText(color) }
      : undefined;

  const headerStyle = colorStyle(chatPrimaryColor);
  const botAvatarStyle = colorStyle(chatSecondaryColor);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 1.5, ...headerStyle }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar variant="rounded" src={logoSrc} sx={{ fontSize: 12 }}>
            Logo
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="div" fontWeight="bold" noWrap>
              {chatTitle}
            </Typography>
            <Typography
              component="div"
              variant="caption"
              color={headerStyle ? "inherit" : "text.secondary"}
              noWrap
            >
              {chatSubtitle}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar src={botAvatarSrc} sx={{ fontSize: 12, ...botAvatarStyle }}>
            Bot
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="div" fontWeight="bold" mb={0.5}>
              {chatBotName}
            </Typography>
            <Box
              sx={{
                display: "inline-block",
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="body2">
                {t(translationPath + "chat_preview_message")}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Typography variant="caption" color="text.secondary">
        {t(translationPath + "chat_preview_hint")}
      </Typography>
    </Stack>
  );
};

export default memo(SiteAppChatProfilePreview);
